import useTranslation from "next-translate/useTranslation";
import Trans from "next-translate/Trans";
import { Flex, Box, Text } from "rebass/styled-components";
import { Label } from "@rebass/forms/styled-components";
import { GoogleReCaptcha, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import PasswordInput from "./PasswordInput";
import { Formik } from "formik";
import { useMutation, useApolloClient } from "@apollo/client";
import gql from "graphql-tag";
import { useState, useContext, useEffect, useCallback } from "react";
import FacebookButton from "./FacebookButton";
import AppleButton, { AppleAuth } from "./AppleButton";
import Separator from "../common/Separator";
import { login as authLogin } from "../../lib/auth";
import { LocaleContext } from "../locale";
import { CurrentUserFragment } from "../../fragments/index.fragments";
import { trackEvent } from "../../lib/event-tracking";
import { useAuthDispatch } from "../../components/common/AuthContext";
import { UserType, ThirdPartyAuth } from "../../enums";
import { addQueryParamToPath, validateEmail } from "../../lib/utils";
import {
  Card,
  Button,
  Input,
  Checkbox,
  TermsOfServiceAgreement,
  Link,
  FullLoader
} from "../common";

const SIGNUP = gql`
  mutation signup(
    $email: String!
    $password: String!
    $passwordConfirmation: String!
    $type: String!
    $recaptchaToken: String!
  ) {
    signUp(
      input: {
        email: $email
        password: $password
        passwordConfirmation: $passwordConfirmation
        type: $type
        recaptchaToken: $recaptchaToken
      }
    ) {
      token
      user {
        ...CurrentUserParts
      }
      errors {
        message
        path
      }
    }
  }
  ${CurrentUserFragment}
`;

const FACEBOOK_SIGNUP = gql`
  mutation authenticateWithFacebook(
    $email: String!
    $facebookToken: String!
    $type: String!
  ) {
    authenticateWithFacebook(
      input: { email: $email, facebookToken: $facebookToken, type: $type }
    ) {
      token
      user {
        ...CurrentUserParts
      }
      errors {
        message
        path
      }
    }
  }
  ${CurrentUserFragment}
`;

const APPLE_SIGNUP = gql`
  mutation authenticateWithApple($input: AuthenticateWithAppleInput!) {
    authenticateWithApple(input: $input) {
      token
      user {
        ...CurrentUserParts
      }
      errors {
        message
        path
      }
    }
  }
  ${CurrentUserFragment}
`;

interface State {
  email?: string;
  password?: string;
  passwordConfirmation?: string;
  thirdPartyToken?: string;
  thirdPartyOption?: ThirdPartyAuth.APPLE | ThirdPartyAuth.FB;
  name?: string;
  surname?: string;
  type: string;
  termsOfService: boolean;
}

interface ErrorObject {
  email: string;
  password: string;
  passwordConfirmation: string;
}

interface Form {
  account: string;
  redirectParams?: any;
}

const onSubmitCallback = (
  signUp,
  apolloClient,
  locale,
  setErrors,
  setSubmitting,
  params = null,
  authDispatch,
  method,
  fetchRecaptchaToken
) => {
  const { token, user, errors } = signUp;

  if (errors.length) {
    setErrors(
      signUp.errors
        .map(({ message, path }) => {
          let mapped = {};
          mapped[`${path.pop()}`] = message;
          return mapped;
        })
        .reduce((acc, current) => {
          acc = { ...acc, ...current };
          return acc;
        })
    );
    setSubmitting(false);
    fetchRecaptchaToken();
  } else {
    trackEvent("user_signed_up", {
      type: user.type,
      signMethod: method
    });

    authDispatch({
      type: "setAccountMode",
      payload: { accountMode: user.type }
    });

    const registrationPath =
      user.type !== UserType.CAREGIVER
        ? "/user/registration"
        : "/caregiver/registration";

    authLogin({
      token,
      user: user,
      apolloClient,
      pathToRedirect:
        params.redirect && params.redirect != "undefined"
          ? addQueryParamToPath(registrationPath, "redirect", params.redirect)
          : registrationPath
    });
  }
};

const validate = (values, errors, t) => {
  if (values?.thirdPartyOption != ThirdPartyAuth.APPLE) {
    if (!values.email) {
      errors.email = !values?.thirdPartyToken
        ? t("signup:required")
        : t("signup:facebookWithoutEmail");
    } else if (!validateEmail(values.email.trim())) {
      errors.email = t("signup:wrongFormat");
    }

    if (!values.thirdPartyToken) {
      if (!values.password) {
        errors.password = t("signup:required");
      } else if (values.password.length < 6) {
        errors.password = t("signup:short");
      }

      if (!values.passwordConfirmation) {
        errors.passwordConfirmation = t("signup:required");
      } else if (values.password !== values.passwordConfirmation) {
        errors.passwordConfirmation = t("signup:noMatch");
      }
    }

    if (!values.termsOfService) {
      errors.termsOfService = t("signup:termsOfServiceRequired");
    }
  }
};

const SignupForm = ({ account, redirectParams }: Form) => {
  const { t } = useTranslation("signup");
  const { locale } = useContext(LocaleContext);
  const authDispatch = useAuthDispatch();
  const [state] = useState<State>({
    email: "",
    password: "",
    passwordConfirmation: "",
    type: account,
    termsOfService: false
  });

  const [recaptchaToken, setRecaptchaToken] = useState<string>();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const fetchRecaptchaToken = useCallback(async () => {
    if (executeRecaptcha) {
      setRecaptchaToken(await executeRecaptcha("signUp"));
    }
  }, [executeRecaptcha]);

  useEffect(() => {
    fetchRecaptchaToken();
  }, [fetchRecaptchaToken]);

  const apolloClient = useApolloClient();
  const [signup, { loading: regularLoading }] = useMutation(SIGNUP);

  const [facebookSignup, { loading: fbLoading }] = useMutation(FACEBOOK_SIGNUP);
  const [appleSignup, { loading: appleLoading }] = useMutation(APPLE_SIGNUP);

  const loading = regularLoading || fbLoading || appleLoading;

  const submit = async (values, { setErrors, setSubmitting }) => {
    if (loading) return;

    let response;
    let signUpResponse;

    switch (values?.thirdPartyOption) {
      case ThirdPartyAuth.APPLE:
        response = await appleSignup({
          variables: {
            input: {
              name: values?.name,
              surname: values?.surname,
              idToken: values.thirdPartyToken,
              type: values.type
            }
          }
        });

        signUpResponse = response.data.authenticateWithApple;

        break;

      case ThirdPartyAuth.FB:
        response = await facebookSignup({
          variables: {
            email: values.email,
            password: null,
            passwordConfirmation: null,
            facebookToken: values.thirdPartyToken,
            type: values.type
          }
        });

        signUpResponse = response.data.authenticateWithFacebook;

        break;

      default:
        response = await signup({
          variables: {
            email: values.email,
            password: values.password,
            passwordConfirmation: values.passwordConfirmation,
            facebookToken: null,
            type: values.type,
            recaptchaToken
          }
        });

        signUpResponse = response.data.signUp;
    }

    const method = values?.thirdPartyOption || "email";

    onSubmitCallback(
      signUpResponse,
      apolloClient,
      locale,
      setErrors,
      setSubmitting,
      redirectParams,
      authDispatch,
      method,
      fetchRecaptchaToken
    );
  };

  return (
    <Card
      variant="box"
      width={1}
      mx="auto"
      px={[0, 0, 2, 4]}
      py={[2, 4]}
      className={"mobile-borderless"}
    >
      <GoogleReCaptcha onVerify={setRecaptchaToken} action="signUp" />
      <Formik
        initialValues={state}
        validate={values => {
          const errors = {} as ErrorObject;
          validate(values, errors, t);
          return errors;
        }}
        onSubmit={submit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          submitForm,
          isSubmitting
        }) => (
          <Box as="form" onSubmit={handleSubmit}>
            {(isSubmitting || loading) && <FullLoader />}
            <Flex flexWrap="wrap">
              <Box mb={2}>
                <Text variant="text.h2" as="h1">
                  <Trans
                    i18nKey="signup:signupForGudog"
                    values={{ role: t(account.toLowerCase()) }}
                  />
                </Text>
              </Box>
              <Box width={1} mb={2}>
                <FacebookButton
                  text={t("signup:signupWithFacebook")}
                  callback={response => {
                    setFieldValue("thirdPartyOption", ThirdPartyAuth.FB);
                    setFieldValue("thirdPartyToken", response.accessToken);
                    if (response.email) setFieldValue("email", response.email);
                    submitForm();
                  }}
                />
              </Box>
              <Box mb={2} width={1}>
                <AppleButton
                  text={t("signup:signupWithApple")}
                  callback={(response: AppleAuth) => {
                    const { authorization, user } = response;
                    setFieldValue("thirdPartyOption", ThirdPartyAuth.APPLE);
                    setFieldValue("thirdPartyToken", authorization.id_token);
                    if (response?.user) {
                      setFieldValue("name", user.name.firstName);
                      setFieldValue("surname", user.name.lastName);
                    }

                    submitForm();
                  }}
                />
              </Box>
              <Separator />
              <Box width={1} variant={errors.email && touched.email && "error"}>
                <Label flexWrap="wrap">
                  <Text width={1} mb={1}>
                    <Trans i18nKey="signup:email" />
                  </Text>
                  <Box width={1}>
                    <Input
                      type="email"
                      name="email"
                      iconRight="mail"
                      placeholder={t("email")}
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      data-test="email"
                    />
                  </Box>
                </Label>
                {errors.email && touched.email && (
                  <Text
                    as="span"
                    data-test="validation"
                  >{`${errors.email}`}</Text>
                )}
              </Box>
              <Box
                width={1}
                mt={2}
                variant={errors.password && touched.password && "error"}
              >
                <Label flexWrap="wrap">
                  <Text width={1} mb={1}>
                    <Trans i18nKey="signup:password" />
                  </Text>
                  <PasswordInput
                    text={t("password")}
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={!!values.thirdPartyOption}
                    data-test="password"
                  />
                </Label>
                {errors.password && touched.password && (
                  <Text
                    as="span"
                    data-test="validation"
                  >{`${errors.password}`}</Text>
                )}
              </Box>
              <Box
                width={1}
                mt={2}
                variant={
                  errors.passwordConfirmation &&
                  touched.passwordConfirmation &&
                  "error"
                }
              >
                <Label flexWrap="wrap">
                  <Text width={1} mb={1}>
                    <Trans i18nKey="signup:confirmPassword" />
                  </Text>
                  <PasswordInput
                    text={t("confirmPassword")}
                    name="passwordConfirmation"
                    value={values.passwordConfirmation}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={!!values.thirdPartyOption}
                    data-test="passwordConfirmation"
                  />
                  {errors.passwordConfirmation &&
                    touched.passwordConfirmation && (
                      <Text
                        as="span"
                        data-test="validation"
                      >{`${errors.passwordConfirmation}`}</Text>
                    )}
                </Label>
              </Box>
              <Box
                mt={2}
                variant={
                  errors.termsOfService && touched.termsOfService && "error"
                }
              >
                <Checkbox
                  label={
                    <TermsOfServiceAgreement
                      i18nKey={"signup:termsOfServiceAgreement"}
                    />
                  }
                  checked={values.termsOfService}
                  onChange={handleChange}
                  name="termsOfService"
                  data-test="terms"
                ></Checkbox>
                {errors.termsOfService && touched.termsOfService && (
                  <Text
                    as="span"
                    data-test="validation"
                  >{`${errors.termsOfService}`}</Text>
                )}
              </Box>
              <Box mt={2} width={1}>
                <Button
                  width={1}
                  size="large"
                  type="submit"
                  disabled={loading}
                  data-test="submit"
                >
                  <Trans i18nKey="header:signup" />
                </Button>
                <Link
                  href={{
                    pathname: "/login",
                    search: {
                      ...redirectParams
                    }
                  }}
                >
                  <Text
                    as="p"
                    color="springGreen"
                    textAlign="center"
                    mt={1}
                    sx={{
                      cursor: "pointer",
                      fontSize: 3
                    }}
                  >
                    <Trans i18nKey="signup:alreadyHaveAcccount" />
                  </Text>
                </Link>
              </Box>
            </Flex>
          </Box>
        )}
      </Formik>
    </Card>
  );
};

export default SignupForm;
